import * as SecureStore from 'expo-secure-store';
import { LoginResponse } from '@/types';

const JWT_TOKEN_KEY = 'vida_jwt_token';
const REFRESH_TOKEN_KEY = 'vida_refresh_token';
const TOKEN_EXPIRES_KEY = 'vida_token_expires';
const LOGIN_RESPONSE_KEY = 'vida_login_response';


/**
 * Store JWT token securely
 * On web, this uses sessionStorage (not persistent across browser sessions)
 * On native, this uses the device's secure storage
 */
export const storeToken = async (token: string): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(JWT_TOKEN_KEY, token);
    } else {
      await SecureStore.setItemAsync(JWT_TOKEN_KEY, token);
    }
  } catch (error) {
    console.error('Error storing token:', error);
    throw new Error('Failed to store authentication token');
  }
};

/**
 * Store refresh token securely
 */
export const storeRefreshToken = async (refreshToken: string): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      await SecureStore.setItemAsync(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch (error) {
    console.error('Error storing refresh token:', error);
    throw new Error('Failed to store refresh token');
  }
};

/**
 * Store token expiration timestamp
 */
export const storeTokenExpires = async (expires: string): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(TOKEN_EXPIRES_KEY, expires);
    } else {
      await SecureStore.setItemAsync(TOKEN_EXPIRES_KEY, expires);
    }
  } catch (error) {
    console.error('Error storing token expiration:', error);
  }
};

/**
 * Retrieve JWT token
 */
export const getToken = async (): Promise<string | null> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(JWT_TOKEN_KEY);
    } else {
      return await SecureStore.getItemAsync(JWT_TOKEN_KEY);
    }
  } catch (error) {
    console.error('Error retrieving token:', error);
    return null;
  }
};

/**
 * Retrieve refresh token
 */
export const getRefreshToken = async (): Promise<string | null> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    } else {
      return await SecureStore.getItemAsync(REFRESH_TOKEN_KEY);
    }
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

/**
 * Retrieve token expiration timestamp
 */
export const getTokenExpires = async (): Promise<string | null> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(TOKEN_EXPIRES_KEY);
    } else {
      return await SecureStore.getItemAsync(TOKEN_EXPIRES_KEY);
    }
  } catch (error) {
    console.error('Error retrieving token expiration:', error);
    return null;
  }
};

/**
 * Store the full login response object as JSON
 */
export const storeLoginResponse = async (loginResponse: LoginResponse): Promise<void> => {
  try {
    const value = JSON.stringify(loginResponse);
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(LOGIN_RESPONSE_KEY, value);
    } else {
      await SecureStore.setItemAsync(LOGIN_RESPONSE_KEY, value);
    }
  } catch (error) {
    console.error('Error storing login response:', error);
    throw new Error('Failed to store login response');
  }
};

/**
 * Remove all auth tokens (logout)
 */
export const removeToken = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(JWT_TOKEN_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRES_KEY);
    } else {
      await SecureStore.deleteItemAsync(JWT_TOKEN_KEY);
      await SecureStore.deleteItemAsync(REFRESH_TOKEN_KEY);
      await SecureStore.deleteItemAsync(TOKEN_EXPIRES_KEY);
    }
  } catch (error) {
    console.error('Error removing tokens:', error);
  }
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getToken();
  return token !== null;
};
